import React, { useEffect, useState } from "react";
import MuxPlayer from "@mux/mux-player-react";
import { AlertTriangle, Clock, Image as ImageIcon, LoaderCircle, Play, X } from "lucide-react";
import { api } from "../lib/api";
import GalleryImage from "./GalleryImage";

export default function GalleryMediaViewer({ picture, onClose, playbackUrl, onWatchFull }) {
  const [playback, setPlayback] = useState(null);
  const [loading, setLoading] = useState(picture?.media_type === "video");
  const [error, setError] = useState("");
  const isVideo = picture?.media_type === "video";
  const posterUrl = picture?.poster_url || picture?.thumbnail_url || "";

  useEffect(() => {
    if (!isVideo || !picture?.id || !playbackUrl) return undefined;
    let active = true;
    setLoading(true);
    setError("");
    api.post(playbackUrl)
      .then(response => { if (active) setPlayback(response.data); })
      .catch(requestError => {
        if (active) setError(requestError?.response?.data?.detail || "Clip playback is not available");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [isVideo, picture?.id, playbackUrl]);

  if (!picture) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/95 p-4 backdrop-blur-sm"
      role="dialog" aria-modal="true" aria-label={picture.title || "Gallery media"}
      onClick={onClose} data-testid="gallery-media-viewer">
      <button type="button" onClick={onClose} aria-label="Close media viewer"
        className="absolute right-4 top-4 z-10 rounded-full bg-black/70 p-3 text-white hover:bg-white/15">
        <X size={22} />
      </button>
      <div className="w-full max-w-5xl overflow-hidden rounded-lg border border-white/10 bg-zinc-950 shadow-2xl"
        onClick={event => event.stopPropagation()}>
        <div className="relative flex aspect-video items-center justify-center bg-black">
          {isVideo && loading && <div className="flex items-center gap-2 text-sm text-zinc-300"><LoaderCircle className="animate-spin" size={18} /> Authorizing clip…</div>}
          {isVideo && error && <div role="alert" className="flex max-w-md flex-col items-center gap-3 p-6 text-center text-amber-100"><AlertTriangle size={28} /><span>{error}</span></div>}
          {isVideo && posterUrl && (!playback || loading || error) && <GalleryImage src={posterUrl} alt={picture.title || "Clip poster"} className="absolute inset-0 h-full w-full object-contain" />}
          {isVideo && playback && !loading && !error && (
            <MuxPlayer playbackId={playback.playback_id} tokens={playback.tokens} streamType="on-demand"
              playsInline style={{ width: "100%", height: "100%" }} data-testid="gallery-mux-player" />
          )}
          {!isVideo && picture.image_url && <GalleryImage src={picture.image_url} alt={picture.title || "Gallery image"} className="h-full w-full object-contain" />}
          {!isVideo && !picture.image_url && <div className="flex flex-col items-center gap-2 text-zinc-400"><ImageIcon size={36} /><span>Image unavailable</span></div>}
        </div>
        <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-white/10 p-4">
          <div className="min-w-0">
            <h2 className="truncate font-semibold text-white">{picture.title || "Gallery media"}</h2>
            {isVideo && Number.isFinite(Number(picture.duration)) && (
              <span className="mt-1 inline-flex items-center gap-1 text-xs text-zinc-400"><Clock size={12} />{formatDuration(picture.duration)}</span>
            )}
          </div>
          {picture.linked_video_id && onWatchFull && (
            <button type="button" onClick={() => onWatchFull(picture.linked_video_id)}
              className="inline-flex items-center gap-2 rounded-md bg-cyan-500/15 px-3 py-2 text-sm text-cyan-100 hover:bg-cyan-500/25"
              data-testid="gallery-watch-full-video">
              <Play size={15} /> Watch full video
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}

export function formatDuration(seconds) {
  const duration = Math.max(0, Math.floor(Number(seconds) || 0));
  const minutes = Math.floor(duration / 60);
  const remainder = String(duration % 60).padStart(2, "0");
  return `${minutes}:${remainder}`;
}
